import { api, clearToken, getCachedUser, getToken, setCachedUser, setToken, shouldSandbox } from './apiClient';
import { getSandboxUser, sandboxSignIn, sandboxSignOut } from './sandbox';

/**
 * Real JWT auth against the Express backend (/api/auth/*).
 * The UI-facing user shape is { id, name, email, role, ... }.
 */

function normalizeUser(u) {
  if (!u) return null;
  return { ...u, id: u.id || u._id };
}

export function loadUser() {
  // Sandbox demo sessions carry no JWT; getSandboxUser restores them from
  // tab-scoped sessionStorage so a direct URL entry / refresh doesn't
  // bounce guarded routes to login.
  const sandboxUser = getSandboxUser();
  if (sandboxUser) return sandboxUser;
  return getToken() ? normalizeUser(getCachedUser()) : null;
}

export function cacheUser(user) {
  if (getToken()) setCachedUser(normalizeUser(user));
}

/** Validate the stored token and refresh the cached user. */
export async function fetchMe() {
  const sandboxUser = getSandboxUser();
  if (sandboxUser) return sandboxUser;
  // NOTE: /api/auth/me returns the user object directly, not { user }.
  const { data } = await api.get('/api/auth/me');
  const user = normalizeUser(data);
  setCachedUser(user);
  return user;
}

export async function login(identifier, password) {
  try {
    const { data } = await api.post('/api/auth/login', { identifier, password });
    setToken(data.token);
    const user = normalizeUser(data.user);
    setCachedUser(user);
    return user;
  } catch (err) {
    // Sandbox builds only: no backend reachable → in-memory demo session.
    // A "vendor…" identifier opens the vendor-side demo and an "admin…"
    // identifier opens the master-dashboard demo - both otherwise unreachable
    // in sandbox (real roles are granted server-side only). The demo admin
    // sees only in-memory data; no real endpoint trusts this session.
    if (shouldSandbox(err)) {
      const role = /^admin/i.test(identifier) ? 'admin' : /^vendor/i.test(identifier) ? 'vendor' : 'customer';
      return sandboxSignIn({ name: identifier.split('@')[0] || 'Sandbox User', email: identifier, role });
    }
    throw err;
  }
}

export async function signUp({ name, email, password, role, phone, termsAccepted }) {
  try {
    const { data } = await api.post('/api/auth/register', {
      name, email, password, role, phone, termsAccepted,
    });
    setToken(data.token);
    const user = normalizeUser(data.user);
    setCachedUser(user);
    return user;
  } catch (err) {
    if (shouldSandbox(err)) {
      return sandboxSignIn({ name, email, role });
    }
    throw err;
  }
}

// Local decode of the ID token payload - display-only sandbox fallback.
// Real verification happens server-side against Google.
function decodeGoogleCredential(credential) {
  try {
    const base64 = credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

/** Google Identity Services credential → VGO session (creates the account if new). */
export async function signInWithGoogle(credential, role) {
  try {
    const { data } = await api.post('/api/auth/google', { credential, role });
    setToken(data.token);
    const user = normalizeUser(data.user);
    setCachedUser(user);
    return user;
  } catch (err) {
    if (shouldSandbox(err)) {
      const profile = decodeGoogleCredential(credential);
      return sandboxSignIn({
        name: profile.name || (profile.email || '').split('@')[0] || 'Sandbox User',
        email: profile.email,
        role,
      });
    }
    throw err;
  }
}

export function signOut() {
  sandboxSignOut();
  clearToken();
}

export async function checkEmail(email) {
  const { data } = await api.get('/api/auth/check-email', { params: { email } });
  return data; // { available, message? }
}

export async function forgotPassword(email) {
  const { data } = await api.post('/api/auth/forgot-password', { email });
  return data;
}

export async function resetPassword(token, password) {
  const { data } = await api.post('/api/auth/reset-password', { token, password });
  return data;
}

export async function updateProfile(fields) {
  const { data } = await api.put('/api/auth/profile', fields);
  const user = normalizeUser(data.user);
  setCachedUser(user);
  return user;
}

export async function changeEmail(newEmail, password) {
  const { data } = await api.put('/api/auth/change-email', { newEmail, password });
  return data;
}

export async function changePassword(currentPassword, newPassword) {
  const { data } = await api.put('/api/auth/change-password', { currentPassword, newPassword });
  // Backend bumps tokenVersion (kills other sessions) and returns a fresh
  // token so this session survives - store it.
  if (data.token) setToken(data.token);
  return data;
}

/** Invalidate every session on every device (tokenVersion bump). */
export async function logoutEverywhere() {
  const { data } = await api.post('/api/auth/logout-everywhere');
  clearToken();
  return data;
}

export async function checkPhone(phone) {
  const { data } = await api.get('/api/auth/check-phone', { params: { phone } });
  return data; // { available, message? }
}

/** Vendor self-reported health rating (grade, permit, inspection dates). */
export async function saveHealthRating(fields) {
  const { data } = await api.put('/api/auth/health-rating', fields);
  return data; // { message, healthRating }
}

export async function saveNotificationPrefs(prefs) {
  const { data } = await api.put('/api/auth/notifications', prefs);
  return data; // { message, notifications }
}

export async function downloadMyData() {
  const { data } = await api.get('/api/auth/my-data');
  return data;
}

export async function deleteAccount(password) {
  const { data } = await api.post('/api/auth/delete-account', { password });
  clearToken();
  return data;
}
